import { captureMessage } from '@sentry/react';
import { SplitFactory } from '@splitsoftware/splitio';
import type {
  IBrowserClient,
  IManager,
} from '@splitsoftware/splitio/types/splitio';
import { pickBy } from 'ramda';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { config } from 'src/config';
import { LocalStorage } from 'src/shared/constants/LocalStorage';
import type { User } from 'src/shared/graphql/generated';
import { PartnerKind } from 'src/shared/graphql/generated';
import type { SplitContext } from 'src/shared/hooks/useSplit';
import { Provider } from 'src/shared/hooks/useSplit';
import { getCookie, setCookie } from 'src/shared/utils/cookie';
import { isBrowser, isMobileApp, isServer } from 'src/shared/utils/environment';
import { isDefined } from 'src/shared/utils/guards';

import { loadMockedFeatures } from './mockedFeatures';

/* ORG SEGMENTS in Split
This setup does not support orgs segments set up in Split.
It passes the userId as the default key, and Split expects the orgId for org flags.
This should eventually be refactored to either use the orgId or userId depending 
on the flag type, but workaround is to use Targeting Rules in Split instead of a segment.
*/

const generateAnonymousId = () => `anonymous-${uuid()}`;

const persistSplitKey = (key: string) => {
  setCookie(LocalStorage.SPLIT_KEY, key);
  localStorage.setItem(LocalStorage.SPLIT_KEY, key);
};

// Checks for local storage first, then for the previously stored cookie, otherwise generates a new key
const getOrCreateSplitKey = () => {
  const key =
    getCookie(LocalStorage.SPLIT_KEY) ||
    localStorage.getItem(LocalStorage.SPLIT_KEY) ||
    generateAnonymousId();

  // Always persist the key so cookie max age is reset
  persistSplitKey(key);

  return key;
};

interface SplitProps extends PropsWithChildren<{}> {}
type UnfilteredAttributes = {
  [key: string]: number | string | boolean | undefined | null;
};

const Split = ({ children }: SplitProps) => {
  const splitFactory = useMemo(() => {
    if (!config.SPLIT_API_KEY || isServer()) return null;

    const key = getOrCreateSplitKey();

    /**
     * @see https://help.split.io/hc/en-us/articles/360020448791-JavaScript-SDK#localhost-mode
     */
    const features =
      isBrowser() && config.SPLIT_API_KEY === 'localhost'
        ? loadMockedFeatures()
        : undefined;

    const factory = SplitFactory({
      core: {
        authorizationKey: config.SPLIT_API_KEY,
        key,
      },
      features,
      startup: {
        readyTimeout: 20,
      },
    });

    return factory;
  }, []);

  const [client, setClient] = useState(splitFactory?.client());
  const [manager] = useState(splitFactory?.manager());
  const [readyClient, setReadyClient] = useState<IBrowserClient | null>(null);
  const [readyManager, setReadyManger] = useState<IManager | null>(null);

  const [attributes, setAttributes] = useState({});

  useEffect(() => {
    setReadyClient(null);

    if (!client) return undefined;

    const onReady = () => setReadyClient(client);

    client.on(client.Event.SDK_READY, onReady);

    return () => {
      client.off(client.Event.SDK_READY, onReady);
    };
  }, [client]);

  useEffect(() => {
    setReadyManger(null);

    if (!manager) return undefined;

    const onReady = () => setReadyManger(manager);

    manager.on(manager.Event.SDK_READY, onReady);

    return () => {
      manager.off(manager.Event.SDK_READY, onReady);
    };
  }, [manager]);

  // Keep the client promise the same across re-renders
  const getClient = useCallback(async () => {
    try {
      // When using async/await syntax with the split client, the rejection
      // should be explicitly propagated or the promise will hang.
      // More details found at https://github.com/splitio/javascript-commons/blob/39af1a1/src/readiness/sdkReadinessManager.ts#L95-L107
      await client?.ready().catch(e => {
        throw e;
      });
    } catch (error) {
      captureMessage((error as Error).message, 'warning');
    }

    return client;
  }, [client]);

  const getManager = useCallback(async () => {
    try {
      await manager?.ready().catch(e => {
        throw e;
      });
    } catch (error) {
      captureMessage((error as Error).message, 'warning');
    }

    return manager;
  }, [manager]);

  const updateUserId = (id: string) => {
    if (
      splitFactory?.client &&
      id !== localStorage.getItem(LocalStorage.SPLIT_KEY)
    ) {
      persistSplitKey(id);
      setClient(splitFactory.client(id));
    }
  };

  const clearUser = useCallback(() => {
    const id = generateAnonymousId();
    persistSplitKey(id);
    if (splitFactory?.client) {
      setClient(splitFactory.client(id));
      setAttributes({});
    }
  }, [splitFactory, setClient, setAttributes]);

  const updateAttributes = (user?: User) => {
    if (user) {
      const org = user.orgs[0];
      const partner = org?.partner;

      const unfilteredAttrs: UnfilteredAttributes = {
        user_id: user?.id,
        email: user?.email,
        org_id: org?.id,
        orgId: org?.id,
        org_priority: org?.priority,
        on_waitlist: org?.onWaitlist,
        onWaitlist: org?.onWaitlist,
        partner_id: partner?.id,
        partnerId: partner?.id,
        is_homeowner: user?.isHomeowner,
        isHomeowner: user?.isHomeowner,
        is_managed: org?.isManaged,
        isManaged: org?.isManaged,
        homeowner: user?.isHomeowner, // This is how this is passed to Split from the mobile repo
        is_insurance: partner?.kind === PartnerKind.Insurance,
        isInsurance: partner?.kind === PartnerKind.Insurance,
        clientApp: 'design-studio',
        app_name: 'web-react',
        appName: 'web-react',
        orgCreatedAt: new Date(org?.createdAt || '').getTime(),
        org_created_at: new Date(org?.createdAt || '').getTime(),
        orgBillingWalletStateRegion: org.wallet?.billingAddressRegion,
        org_billing_wallet_state_region: org.wallet?.billingAddressRegion,
      };

      if (isMobileApp() && window.HOVERApp?.version) {
        unfilteredAttrs.app_version = window.HOVERApp.version;
        unfilteredAttrs.device_os = window.HOVERApp.platform.name;
        unfilteredAttrs.device_os_version = window.HOVERApp.platform.version;
      }

      const valueIsDefined = (_: any, key: string | number) =>
        isDefined(unfilteredAttrs[key]);
      const attr: SplitIO.Attributes = pickBy(valueIsDefined, unfilteredAttrs);
      if (JSON.stringify(attributes) !== JSON.stringify(attr)) {
        setAttributes(attr);
      }
    }
  };

  const contextValue: SplitContext = {
    attributes,
    clientPromise: getClient(),
    client: readyClient,
    managerPromise: getManager(),
    manager: readyManager,
    updateAttributes,
    updateUserId,
    clearUser,
  };

  return <Provider value={contextValue}>{children}</Provider>;
};

export { Split, generateAnonymousId, getOrCreateSplitKey };
